/// <reference path="../../types/axios-fix.d.ts" />
import axios, { AxiosProgressEvent } from 'axios';
import { httpClientService } from './core';
import { UploadError, type UploadErrorCode } from './upload-errors';

/**
 * Phase 2.5 Direct R2 Upload Service
 * 
 * This service implements the new direct R2 access pattern using presigned URLs,
 * eliminating streaming through services for better performance and scalability.
 * 
 * Architecture Change:
 * Before: Client → Gateway → Content Service → R2
 * After:  Client → Gateway (presigned URL) → Direct R2 Access
 */

// Types for presigned URL requests/responses
export interface PresignedUploadRequest {
  fileName: string;
  fileSize: number;
  contentType: string;
  organizationId: string;
  teamId: string;
  metadata?: {
    title?: string;
    description?: string;
    content_type?: string;
    tag_group_values?: Record<string, string[]>;
  };
  uploaderAffirmation?: {
    confirmed: boolean;
    timestamp: Date;
    ipAddress?: string;
    termsVersion: string;
    policyVersion: string;
  };
}

export interface PresignedUploadResponse {
  success: boolean;
  data: {
    content_id: string;
    presigned_url: string;
    expires_in: number;
    key?: string;
    method?: string;
    fields?: Record<string, string>;
  };
  message?: string;
  metadata?: {
    timestamp: string;
    requestId: string;
    version: string;
  };
}

export interface PresignedDownloadResponse {
  data: {
    presigned_url: string;
    expires_in: number;
    content_id: string;
    file_name?: string;
    file_size?: string;
    mime_type?: string;
  };
  metadata?: {
    timestamp: string;
    requestId: string;
    version: string;
  };
}

// Progress tracking interfaces
export interface DirectUploadProgress {
  fileId: string;
  fileName: string;
  progress: number; // 0-100
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  uploadedBytes: number;
  totalBytes: number;
  speed: number; // bytes per second
  contentId?: string;
}

export class UploadDirectService {
  constructor() {}

  /**
   * Get presigned upload URL from Gateway
   * This replaces the chunked upload initiation with a single presigned URL request
   */
  async getPresignedUploadUrl(request: PresignedUploadRequest): Promise<PresignedUploadResponse> {
    try {
      const payload: any = {
        fileName: request.fileName,
        fileSize: request.fileSize,
        mimeType: request.contentType,
        organizationId: request.organizationId,
        teamId: request.teamId,
        metadata: request.metadata ? this.serializeMetadata(request.metadata) : undefined,
      };

      // Include uploader affirmation if provided (required for GDPR/CCPA compliance)
      if (request.uploaderAffirmation) {
        payload.uploaderAffirmation = {
          confirmed: request.uploaderAffirmation.confirmed,
          timestamp: request.uploaderAffirmation.timestamp.toISOString(),
          ipAddress: request.uploaderAffirmation.ipAddress || '',
          termsVersion: request.uploaderAffirmation.termsVersion,
          policyVersion: request.uploaderAffirmation.policyVersion,
        };
      }

      const response = await httpClientService.post<PresignedUploadResponse>(
        '/content/upload/presigned-url',
        payload
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to get presigned upload URL:', error);
      const backendCode: string | undefined = error.response?.data?.error?.code;
      const backendMsg: string | undefined = error.response?.data?.error?.message;
      let code: UploadErrorCode = 'PRESIGN_FAILED';
      if (backendCode === 'UNSUPPORTED_FILE_TYPE') code = 'UNSUPPORTED_FILE_TYPE';
      else if (backendCode === 'BLOCKED_FILE_TYPE') code = 'BLOCKED_FILE_TYPE';
      else if (backendCode === 'FILE_TOO_LARGE') code = 'FILE_TOO_LARGE';
      else if (backendCode === 'PERMISSION_DENIED') code = 'PERMISSION_DENIED';
      else if (backendCode === 'AFFIRMATION_REQUIRED') code = 'AFFIRMATION_REQUIRED';
      else if (backendCode === 'QUOTA_EXCEEDED') code = 'QUOTA_EXCEEDED';
      throw new UploadError(code, backendMsg || 'Failed to start upload');
    }
  }

  /**
   * Get presigned download URL from Gateway
   */
  async getPresignedDownloadUrl(
    contentId: string,
    teamId: string,           // REQUIRED - team context for subscription validation
    isDownload: boolean = false
  ): Promise<PresignedDownloadResponse> {
    try {
      const params = new URLSearchParams();
      if (isDownload) {
        params.append('download', 'true');
      }
      params.append('team_id', teamId);

      const response = await httpClientService.get<PresignedDownloadResponse>(
        `/content/${contentId}/presigned-download-url?${params.toString()}`
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to get presigned download URL:', error);
      throw new Error(error.response?.data?.error?.message || 'Failed to get presigned download URL');
    }
  }

  /**
   * Get presigned download URL for a specific content version
   */
  async getVersionPresignedUrl(
    contentId: string,
    versionNumber: number,
    teamId: string,
  ): Promise<{ presigned_url: string; mime_type?: string; file_name?: string }> {
    try {
      const params = new URLSearchParams();
      params.append('team_id', teamId);
      params.append('version_number', String(versionNumber));

      const response = await httpClientService.get<any>(
        `/content/${contentId}/presigned-download-url?${params.toString()}`
      );

      return response.data?.data ?? response.data;
    } catch (error: any) {
      console.error('Failed to get version presigned URL:', error);
      throw new Error(error.response?.data?.error?.message || 'Failed to get version presigned URL');
    }
  }

  /**
   * Upload file directly to R2 using presigned URL
   * This bypasses the Gateway/Content Service streaming entirely
   *
   * Uses activity-based timeout: only fails if no progress for STALL_TIMEOUT seconds,
   * allowing large files to upload successfully as long as bytes keep flowing.
   *
   * IMPORTANT: contentType must match exactly what was used to generate the presigned URL,
   * otherwise R2 will reject the upload with a signature mismatch error.
   */
  async uploadFileDirectly(
    file: File,
    presignedUrl: string,
    contentType: string,
    fields: Record<string, string> = {},
    onProgress?: (progress: DirectUploadProgress) => void,
    retryCount: number = 0,
    abortSignal?: AbortSignal,
  ): Promise<void> {
    const startTime = Date.now();
    let lastProgressTime = Date.now();
    let lastLoadedBytes = 0;
    const STALL_TIMEOUT_MS = 30_000;
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1_000;

    // Wrap caller signal so we can abort from inside the stall monitor too.
    const internalAbort = new AbortController();
    if (abortSignal) {
      if (abortSignal.aborted) internalAbort.abort();
      else abortSignal.addEventListener('abort', () => internalAbort.abort());
    }

    let stalled = false;
    const activityCheck = setInterval(() => {
      if (Date.now() - lastProgressTime > STALL_TIMEOUT_MS) {
        stalled = true;
        clearInterval(activityCheck);
        console.error('Upload stalled — aborting', {
          fileName: file.name, lastLoadedBytes, totalBytes: file.size,
        });
        internalAbort.abort();
      }
    }, 5_000);

    try {
      await axios.put(presignedUrl, file, {
        headers: { 'Content-Type': contentType },
        onUploadProgress: (e: AxiosProgressEvent) => {
          const loaded = e.loaded || 0;
          const total = e.total || file.size;
          if (loaded > lastLoadedBytes) {
            lastProgressTime = Date.now();
            lastLoadedBytes = loaded;
          }
          const elapsed = (Date.now() - startTime) / 1000;
          onProgress?.({
            fileId: file.name,
            fileName: file.name,
            progress: Math.round((loaded / total) * 100),
            status: 'uploading',
            uploadedBytes: loaded,
            totalBytes: total,
            speed: elapsed > 0 ? loaded / elapsed : 0,
          });
        },
        timeout: 0,
        signal: internalAbort.signal,
      } as any);

      clearInterval(activityCheck);
      onProgress?.({
        fileId: file.name, fileName: file.name, progress: 100, status: 'completed',
        uploadedBytes: file.size, totalBytes: file.size, speed: 0,
      });
    } catch (err: any) {
      clearInterval(activityCheck);

      // Map axios failure to a structured UploadError.
      let code: UploadErrorCode;
      let message: string;

      if (stalled) {
        code = 'UPLOAD_STALLED';
        message = `Upload stalled — no progress for ${STALL_TIMEOUT_MS / 1000}s`;
      } else if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
        code = 'UPLOAD_CANCELLED';
        message = 'Upload cancelled';
      } else if (!err.response) {
        // No response received — network or CORS failure.
        code = 'NETWORK_ERROR';
        message = err.message || 'Network error during upload';
      } else if (err.response.status === 0) {
        code = 'CORS_REJECTED';
        message = 'Server rejected the upload (CORS)';
      } else if (err.response.status === 403) {
        code = 'SIGNATURE_MISMATCH';
        message = 'R2 rejected the upload — signature mismatch';
      } else if (err.response.status === 500 && retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
        console.warn(`R2 500, retry ${retryCount + 1}/${MAX_RETRIES} in ${delay}ms`);
        await new Promise((r) => setTimeout(r, delay));
        return this.uploadFileDirectly(file, presignedUrl, contentType, fields, onProgress, retryCount + 1, abortSignal);
      } else {
        code = 'NETWORK_ERROR';
        message = `Upload failed: HTTP ${err.response.status}`;
      }

      // Rich diagnostic logging — helps narrow down browser-side failures
      // (CORS, extensions, signature mismatch, etc.) from the DevTools console.
      let presignedHost: string | undefined;
      try { presignedHost = new URL(presignedUrl).host; } catch { /* keep undefined */ }
      console.error('Direct R2 upload failed', {
        fileName: file.name,
        code,
        message,
        errorStatus: err.response?.status,
        errorCode: err.code,
        errorName: err.name,
        axiosMessage: err.message,
        responseHeaders: err.response?.headers,
        presignedHost,
        contentType,
        uploadedBytes: lastLoadedBytes,
        retryCount,
        rawError: err,
      });

      onProgress?.({
        fileId: file.name, fileName: file.name, progress: 0, status: 'error',
        error: message, uploadedBytes: 0, totalBytes: file.size, speed: 0,
      });

      throw new UploadError(code, message, {
        status: err.response?.status,
        uploadedBytes: lastLoadedBytes,
      });
    }
  }

  /**
   * Complete upload flow: get presigned URL and upload directly to R2
   * Pass an optional AbortController to allow cancellation
   */
  async uploadFile(
    file: File,
    organizationId: string,
    teamId: string,
    metadata?: PresignedUploadRequest['metadata'],
    onProgress?: (progress: DirectUploadProgress) => void,
    uploaderAffirmation?: PresignedUploadRequest['uploaderAffirmation'],
    abortController?: AbortController,
    useAiTitle?: boolean,
    aiTagMode?: 'off' | 'replace' | 'merge',
    useAiDescription?: boolean,
  ): Promise<string> { // Returns content ID

    // Normalize content type - use exact value for consistency
    const contentType = file.type || 'application/octet-stream';

    // Pre-flight: if this looks like an archive by extension or MIME, verify
    // the magic bytes match the declared format before creating a content
    // record. This catches the "claims to be a .zip but isn't" case that
    // otherwise surfaces as a network timeout much later in the flow.
    await this.sniffArchiveIfApplicable(file);

    let content_id: string | undefined;

    try {
      // 1. Get presigned upload URL from Gateway (creates PENDING_UPLOAD metadata)
      const presignedResponse = await this.getPresignedUploadUrl({
        fileName: file.name,
        fileSize: file.size,
        contentType, // Use normalized content type
        organizationId,
        teamId,
        metadata,
        uploaderAffirmation,
      });

      const { content_id: contentId, presigned_url, fields } = presignedResponse.data;
      content_id = contentId;

      // 2. Upload directly to R2 using presigned URL
      // CRITICAL: Pass the same contentType that was used to generate the presigned URL
      await this.uploadFileDirectly(
        file,
        presigned_url,
        contentType,
        fields || {},
        (progress) => {
          // Add content ID to progress updates
          const enhancedProgress = { ...progress, contentId: content_id };
          onProgress?.(enhancedProgress);
        },
        0, // retryCount
        abortController?.signal
      );

      // 3. Complete the upload (verify file and mark as READY)
      await this.completeUpload(content_id, { useAiTitle, aiTagMode, useAiDescription });
      console.log(`Content ${content_id} upload completed and verified successfully`);

      return content_id;
    } catch (error) {
      // Upload failed or was cancelled - clean up metadata
      if (content_id) {
        const isCancelled = error instanceof UploadError && error.code === 'UPLOAD_CANCELLED';
        console.warn(`Upload ${isCancelled ? 'cancelled' : 'failed'} for ${file.name}, cleaning up metadata`, error);
        await this.cancelUpload(content_id, `Upload ${isCancelled ? 'cancelled by user' : 'failed'}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
      throw error;
    }
  }

  /**
   * Complete upload after successful R2 upload
   * This verifies the file exists and updates the content status
   */
  async completeUpload(contentId: string, options?: { useAiTitle?: boolean; useAiDescription?: boolean; aiTagMode?: 'off' | 'replace' | 'merge' }): Promise<void> {
    try {
      await httpClientService.put(
        `/content/${contentId}/complete-upload`,
        {
          ...(options?.useAiTitle !== undefined && { use_ai_title: options.useAiTitle }),
          ...(options?.useAiDescription !== undefined && { use_ai_description: options.useAiDescription }),
          ...(options?.aiTagMode !== undefined && { ai_tag_mode: options.aiTagMode }),
        }
      );
    } catch (error: any) {
      console.error(`Failed to complete upload for ${contentId}:`, error);
      const backendCode: string | undefined = error.response?.data?.error?.code;
      const code: UploadErrorCode = backendCode === 'UPLOAD_INCOMPLETE' ? 'UPLOAD_INCOMPLETE' : 'PROCESSING_FAILED';
      throw new UploadError(code, error.response?.data?.error?.message || 'Failed to complete upload');
    }
  }

  /**
   * Cancel upload and cleanup partial files
   */
  async cancelUpload(contentId: string, reason?: string): Promise<void> {
    try {
      await httpClientService.delete(
        `/content/${contentId}/cancel-upload`,
        { data: { reason } }
      );
    } catch (error: any) {
      console.error(`Failed to cancel upload for ${contentId}:`, error);
      // Don't throw - cancellation is best-effort cleanup
    }
  }

  /**
   * Publish content after successful upload
   * This changes the status from DRAFT to PUBLISHED
   */
  async publishContent(contentId: string): Promise<void> {
    try {
      await httpClientService.put(
        `/content/${contentId}/publish`,
        {}
      );
    } catch (error: any) {
      console.error(`Failed to publish content ${contentId}:`, error);
      throw new Error(error.response?.data?.error?.message || 'Failed to publish content');
    }
  }

  /**
   * Download file using presigned URL
   * This provides a direct download URL that bypasses the Gateway streaming
   */
  async downloadFile(contentId: string, teamId: string): Promise<{url: string, fileName?: string}> {
    const presignedResponse = await this.getPresignedDownloadUrl(contentId, teamId, true); // Pass true for download

    return {
      url: presignedResponse.data.presigned_url,
      fileName: presignedResponse.data.file_name,
    };
  }

  /**
   * Download file as blob using presigned URL
   * This fetches the file directly from R2 and returns as a blob
   */
  async downloadFileAsBlob(contentId: string, teamId: string): Promise<{blob: Blob, fileName?: string}> {
    const { url, fileName } = await this.downloadFile(contentId, teamId);
    
    try {
      const response = await axios.get(url, {
        responseType: 'blob',
        timeout: 60000, // 60 second timeout
      });
      
      return {
        blob: response.data as Blob,
        fileName,
      };
    } catch (error: any) {
      console.error('Failed to download file from R2:', error);
      throw new Error(`Download failed: ${error.message}`);
    }
  }

  /**
   * Pre-flight archive sanity check.
   *
   * If the file claims to be one of our supported archive formats (by MIME
   * type OR file extension), read the first 512 bytes and verify the format's
   * magic bytes are present. If the magic bytes don't match the declared
   * format — corrupted file, wrong extension, truncated download, etc. —
   * throw UploadError(CORRUPT_ARCHIVE) BEFORE creating any backend state.
   *
   * This is fast: file.slice(0, 512) returns a Blob that hasn't loaded the
   * data yet; arrayBuffer() only reads the requested range, not the whole
   * file. A 5 GB zip takes the same time to sniff as a 5 KB one.
   *
   * Magic bytes:
   *   - ZIP: PK\x03\x04 (file), PK\x05\x06 (empty), PK\x07\x08 (spanned)
   *   - GZIP: 1F 8B 08 (the 08 confirms deflate, the only supported method)
   *   - TAR: "ustar" at offset 257 (ustar header is the standard since POSIX.1-1988)
   */
  private async sniffArchiveIfApplicable(file: File): Promise<void> {
    const mime = (file.type || '').toLowerCase();
    const name = file.name.toLowerCase();

    const isZip =
      mime === 'application/zip' ||
      mime === 'application/x-zip-compressed' ||
      name.endsWith('.zip');
    const isGzip =
      mime === 'application/gzip' ||
      mime === 'application/x-gzip' ||
      name.endsWith('.tar.gz') ||
      name.endsWith('.tgz') ||
      name.endsWith('.gz');
    const isTar =
      mime === 'application/x-tar' ||
      (name.endsWith('.tar') && !isGzip);

    if (!isZip && !isGzip && !isTar) return; // Not an archive — nothing to check

    if (file.size === 0) {
      throw new UploadError('CORRUPT_ARCHIVE', 'Archive file is empty', {
        fileName: file.name, fileSize: 0,
      });
    }

    let head: Uint8Array;
    try {
      const buffer = await file.slice(0, 512).arrayBuffer();
      head = new Uint8Array(buffer);
    } catch (err) {
      // Reading the file failed — surface as corrupt so the user retries with
      // a different file. (Reading from a File object essentially never fails
      // in practice, so this is defensive.)
      throw new UploadError('CORRUPT_ARCHIVE', `Couldn't read file head: ${err instanceof Error ? err.message : 'unknown error'}`);
    }

    if (isZip) {
      const ok =
        head.length >= 4 &&
        head[0] === 0x50 && head[1] === 0x4B &&
        ((head[2] === 0x03 && head[3] === 0x04) ||  // standard local file header
         (head[2] === 0x05 && head[3] === 0x06) ||  // empty archive (end-of-central-dir only)
         (head[2] === 0x07 && head[3] === 0x08));   // spanned-archive marker (rare)
      if (!ok) {
        throw new UploadError('CORRUPT_ARCHIVE',
          `${file.name} does not have a valid ZIP signature`,
          { fileName: file.name, fileSize: file.size, declaredFormat: 'zip' },
        );
      }
      return;
    }

    if (isGzip) {
      const ok =
        head.length >= 3 &&
        head[0] === 0x1F && head[1] === 0x8B && head[2] === 0x08;
      if (!ok) {
        throw new UploadError('CORRUPT_ARCHIVE',
          `${file.name} does not have a valid GZIP signature`,
          { fileName: file.name, fileSize: file.size, declaredFormat: 'gzip' },
        );
      }
      return;
    }

    if (isTar) {
      // Tar has no magic at offset 0; the "ustar" indicator lives at offset
      // 257 inside the first 512-byte header block. Older pre-POSIX tars
      // (v7 tar) had no magic at all — we'd reject those, but they're
      // essentially extinct in modern workflows.
      if (head.length < 263) {
        throw new UploadError('CORRUPT_ARCHIVE',
          `${file.name} is too small to be a valid tar archive`,
          { fileName: file.name, fileSize: file.size, declaredFormat: 'tar' },
        );
      }
      const magic = String.fromCharCode(head[257], head[258], head[259], head[260], head[261]);
      if (magic !== 'ustar') {
        throw new UploadError('CORRUPT_ARCHIVE',
          `${file.name} does not have a valid TAR header signature`,
          { fileName: file.name, fileSize: file.size, declaredFormat: 'tar' },
        );
      }
      return;
    }
  }

  /**
   * Serialize metadata for API compatibility
   */
  private serializeMetadata(metadata: Record<string, any>): Record<string, string> {
    const serialized: Record<string, string> = {};
    Object.entries(metadata).forEach(([key, value]) => {
      if (typeof value === 'string') {
        serialized[key] = value;
      } else {
        serialized[key] = JSON.stringify(value);
      }
    });
    return serialized;
  }

  /**
   * Utility function to format file size
   */
  formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${Math.round(size * 100) / 100} ${units[unitIndex]}`;
  }

  /**
   * Utility function to format upload speed
   */
  formatSpeed(bytesPerSecond: number): string {
    return `${this.formatFileSize(bytesPerSecond)}/s`;
  }

  /**
   * Get a presigned URL for uploading a version file.
   * Does NOT create a new Content record — just returns a storage key and upload URL.
   */
  async getVersionUploadUrl(
    contentId: string,
    teamId: string,
    fileName: string,
    mimeType: string,
    fileSize: number
  ): Promise<{ presignedUrl: string; storageKey: string; expiresIn: number }> {
    try {
      const response = await httpClientService.post<any>(
        `/content/${contentId}/versions/upload-url`,
        { file_name: fileName, mime_type: mimeType, file_size: fileSize, team_id: teamId }
      );
      // Response is { data: { presignedUrl, storageKey, expiresIn } }
      return response.data.data ?? response.data;
    } catch (error: any) {
      console.error(`Failed to get version upload URL for content ${contentId}:`, error);
      throw new Error(error.response?.data?.error?.message || 'Failed to get version upload URL');
    }
  }

  /**
   * Upload a version file and return the storage key.
   * Uses the new version-specific presigned URL endpoint.
   */
  async uploadVersionFile(
    contentId: string,
    teamId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<string> {
    const { presignedUrl, storageKey } = await this.getVersionUploadUrl(
      contentId,
      teamId,
      file.name,
      file.type || 'application/octet-stream',
      file.size
    );

    await this.uploadFileDirectly(
      file,
      presignedUrl,
      file.type || 'application/octet-stream',
      {},
      (progress) => onProgress?.(progress.progress)
    );

    return storageKey;
  }
}

// Export singleton instance
export const uploadDirectService = new UploadDirectService();
export default uploadDirectService;
